import { useState, useEffect, useCallback } from 'react';
import { WelcomeBanner, Widgets, ContinueCourses, LearnMore, RightSidebar } from '@/components/dashboard';
import { IonContent, IonPage } from '@ionic/react';
import { useProfileStore } from '@/store/useProfileStore';
import { useIonRouter } from '@ionic/react';
import { progressService } from '@/services/progressService';
import { courseService } from '@/services/courseService';
import { additionalContentService } from '@/services/additionalContentService';
import { TeamUserSummary } from '@/types/teamUserSummary';
import { Certification } from '@/types/certification';
import { AdditionalContent } from '@/types/additionalContent';
import { useRefresh } from '@/contexts/RefreshContext';
import { getCourseInfo } from '@/utils/courseUtils';
import { useBadgeDetection } from '@/hooks/useBadgeDetection';

const Dashboard = () => {
  const profile = useProfileStore((state) => state.activeProfile);
  const router = useIonRouter();
  const [widgetData, setWidgetData] = useState<TeamUserSummary | null>(null);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [additionalContent, setAdditionalContent] = useState<AdditionalContent[]>([]);
  const [loading, setLoading] = useState(true);
  const { registerRefreshHandler, unregisterRefreshHandler } = useRefresh();

  // Refetch data when navigating to this page
  useEffect(() => {
    const fetchData = async () => {
      if (!profile?.id) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const [summaryData, certs, additionalContentData] = await Promise.all([
          progressService.getTeamUserSummary(profile.id),
          courseService.getCertificationsWithCourses(),
          additionalContentService.searchAdditionalContent({ page: 'home', slot: 'learn_more' })
        ]);
        setWidgetData(summaryData);
        setCertifications(certs);
        setAdditionalContent(additionalContentData);
      } catch (err) {
        // Error modal presented by API
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [profile?.id, router.routeInfo.pathname]);

  const handleRefresh = useCallback(async () => {
    if (!profile?.id) return;

    try {
      setLoading(true);
      const [summaryData, certs, additionalContentData] = await Promise.all([
        progressService.getTeamUserSummary(profile.id),
        courseService.getCertificationsWithCourses(),
        additionalContentService.searchAdditionalContent({ page: 'home', slot: 'learn_more' })
      ]);
      setWidgetData(summaryData);
      setCertifications(certs);
      setAdditionalContent(additionalContentData);
    } catch (err) {
      // Error modal presented by API
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  // Register refresh handler for Test Panel
  useEffect(() => {
    registerRefreshHandler('/dashboard', handleRefresh);
    return () => {
      unregisterRefreshHandler('/dashboard');
    };
  }, [registerRefreshHandler, unregisterRefreshHandler, handleRefresh]);

  // Detect newly earned badges
  useBadgeDetection(widgetData?.badges);

  // Calculate course info from certifications with courses
  const courseData = getCourseInfo(certifications);

  // Determine if we should show "Welcome back" or just "Welcome"
  const shouldShowBack = () => {
    if (!profile) return true;
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    const dateToCheck = profile.invitationAcceptedAt
      ? new Date(profile.invitationAcceptedAt)
      : new Date(profile.createdAt);
    return dateToCheck < oneHourAgo;
  };

  return (
    <IonPage>
      <IonContent className="bg-gray-50">
        <div className="gap-2.5 flex max-md:flex-col max-md:items-stretch">
          <div className="flex-1 lg:w-[73%] max-md:w-full min-w-0">
            <div className="grow max-md:max-w-full">
              <div data-tour="dashboard-scorecard">
                <WelcomeBanner
                  userName={profile?.displayName ?? ''}
                  showBack={shouldShowBack()}
                  data={courseData}
                />
                <Widgets data={widgetData ?? undefined} />
              </div>
              <ContinueCourses data={courseData} />
              <LearnMore content={additionalContent} />
            </div>
          </div>
          <RightSidebar className="hidden md:block" />
        </div>
      </IonContent>
    </IonPage>
  );
};

export default Dashboard;